import StockCount from "../../../../utilities/data/model/StockCount";
import {
  useCurrentCountContext,
  useStockCountSearchContext,
  useStockCountSyncContext,
} from "../../../../utilities/stock-count/hooks";
import { useStockCountScreenContextProvider } from "./hooks";

export function getStockCountItemCountObservables({
  stockCount,
}: {
  stockCount: StockCount;
}) {
  return {
    allCount: stockCount.items.observeCount(false),
  };
}

interface WithStockCountScreenContextProps {}

export function withStockCountScreenContext<
  T extends WithStockCountScreenContextProps = WithStockCountScreenContextProps
>(WrappedComponent: React.ComponentType<T>) {
  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithTheme = (
    props: Omit<T, keyof WithStockCountScreenContextProps>
  ) => {
    // Fetch the props you want to inject. This could be done with context instead.
    const context = useStockCountScreenContextProvider();

    // props comes afterwards so the can override the default ones.
    return <WrappedComponent {...context} {...(props as T)} />;
  };

  ComponentWithTheme.displayName = `withStockCountScreenContext(${getDisplayName(
    WrappedComponent
  )})`;
  return ComponentWithTheme;
}

interface WithCurrentCountScreenContextProps {}

export function withCurrentCountContext<
  T extends WithCurrentCountScreenContextProps = WithCurrentCountScreenContextProps
>(WrappedComponent: React.ComponentType<T>) {
  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithTheme = (
    props: Omit<T, keyof WithStockCountScreenContextProps>
  ) => {
    // Fetch the props you want to inject. This could be done with context instead.
    const context = useCurrentCountContext();

    // props comes afterwards so the can override the default ones.
    return <WrappedComponent {...context} {...(props as T)} />;
  };

  ComponentWithTheme.displayName = `withCurrentCountContext(${getDisplayName(
    WrappedComponent
  )})`;
  return ComponentWithTheme;
}

interface WithStockCountSearchContextProps {}

export function withStockCountSearchContext<
  T extends WithStockCountSearchContextProps = WithStockCountSearchContextProps
>(WrappedComponent: React.ComponentType<T>) {
  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithTheme = (
    props: Omit<T, keyof WithStockCountSearchContextProps>
  ) => {
    // Fetch the props you want to inject. This could be done with context instead.
    const context = useStockCountSearchContext();

    // props comes afterwards so the can override the default ones.
    return <WrappedComponent {...context} {...(props as T)} />;
  };

  ComponentWithTheme.displayName = `withStockCountSearchContext(${getDisplayName(
    WrappedComponent
  )})`;
  return ComponentWithTheme;
}

export function withStockCountSyncContext<T>(
  WrappedComponent: React.ComponentType<T>
) {
  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithTheme = (
    props: Omit<T, keyof WithStockCountSearchContextProps>
  ) => {
    // Fetch the props you want to inject. This could be done with context instead.
    const context = useStockCountSyncContext();

    // props comes afterwards so the can override the default ones.
    return <WrappedComponent {...context} {...(props as T)} />;
  };

  ComponentWithTheme.displayName = `withStockCountSyncContext(${getDisplayName(
    WrappedComponent
  )})`;
  return ComponentWithTheme;
}

function getDisplayName<T>(WrappedComponent: React.ComponentType<T>) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || "Component";

  return displayName;
}
